<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('2fa-setup-phone-title') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <div class="setup-form">
        <p class="hint">
          {{ t('2fa-setup-phone-prompt') }}
        </p>

        <Form
          @submit="onSubmit"
          :validation-schema="schema"
          :initial-values="{ phoneNumberDir: '+48', phoneNumber: '' }"
          v-slot="{ errors, isSubmitting }"
        >
          <div class="form-group row mb-1 phone-row">
            <label class="col-sm-3 col-form-label">{{ t('phone-number') }}</label>
            <div class="col-sm-3 pe-0">
              <Field
                name="phoneNumberDir"
                type="text"
                maxlength="3"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumberDir }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumberDir }}</div>
            </div>
            <div class="col-sm-6 ps-1">
              <Field
                name="phoneNumber"
                type="text"
                inputmode="numeric"
                maxlength="9"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumber }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumber }}</div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center mt-3">
            <div class="col-auto">
              <button class="btn btn-outline-secondary" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('2fa-setup-phone-submit') }}
              </button>
            </div>
            <div class="col-auto mt-2">
              <button type="button" class="btn btn-link btn-sm text-muted" @click="onCancel">
                {{ t('2fa-cancel') }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const notify = useNotificationStore();

const schema = Yup.object({
  phoneNumberDir: Yup.string()
    .matches(/^\+\d{2}$/, t('phone-prefix-invalid'))
    .required(t('phone-prefix-required')),
  phoneNumber: Yup.string()
    .matches(/^\d{9}$/, t('phone-number-invalid'))
    .required(t('phone-number-required')),
});

onMounted(() => {
  // Wejście wprost na URL bez stanu pendingTwoFactor = sesja BE nie czeka na ustawienie
  // numeru. Odsyłamy na /login.
  if (!authStore.pendingTwoFactor || !authStore.pendingTwoFactor.requiresPhoneSetup) {
    router.replace('/login');
  }
});

async function onSubmit(values) {
  try {
    const res = await authStore.setupTwoFactorPhone(values.phoneNumberDir, values.phoneNumber);
    if (res && res.dispatchFailed) {
      notify.error(t('2fa-dispatch-failed'));
    }
    // Po sukcesie store przekierowuje na /login/2fa.
  } catch (e) {
    notify.error(typeof e === 'string' ? e : t('2fa-setup-phone-failed'));
  }
}

function onCancel() {
  authStore.clearPendingTwoFactor();
  authStore.logout();
}
</script>

<style scoped>
.card-body {
  background: var(--surface-secondary);
}
.setup-form {
  width: 460px;
  max-width: 100%;
}
.hint {
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}
.btn-outline-secondary {
  border: 1px solid grey;
  color: var(--text-primary);
  border-radius: var(--control-radius);
  padding: 4px 16px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}</style>
